import { useEffect } from 'react';
import { create } from 'zustand';
import { flagOn, type FlagKey, type FlagRow } from '@il-colpo/meta/progress';
import { getAccountStore, useAccount } from '../account/accountStore.ts';
import type { LiveSnapshot } from './types.ts';

/**
 * What the team can change without a release, as this page knows it: the feature flags and the
 * remote configuration. Read from the service once the player is signed in, and again after five
 * minutes at most; until then (and with no account, no network or no answer) the app's own defaults
 * stand (`FLAG_DEFAULTS`), so a feature is never lost because a request was.
 */

const FRESH_MS = 5 * 60_000;

interface LiveState {
  flags: FlagRow[] | null;
  config: Record<string, unknown>;
  loadedAt: number;
  loading: boolean;
  /** Reads them again if they are older than five minutes (or `force`). */
  refresh: (force?: boolean) => Promise<void>;
  reset: () => void;
}

export const useLiveStore = create<LiveState>((set, get) => ({
  flags: null,
  config: {},
  loadedAt: 0,
  loading: false,
  refresh: async (force = false) => {
    const { loading, loadedAt } = get();
    if (loading || (!force && Date.now() - loadedAt < FRESH_MS)) return;
    const client = getAccountStore().getState().client;
    if (!client) return;
    set({ loading: true });
    try {
      const r = await client.meta().live();
      if (r.ok) set({ flags: r.value.flags, config: r.value.config, loadedAt: Date.now() });
      else set({ loadedAt: Date.now() - FRESH_MS + 30_000 });
    } finally {
      set({ loading: false });
    }
  },
  reset: () => {
    set({ flags: null, config: {}, loadedAt: 0, loading: false });
  },
}));

/** Reads the flags and the configuration once the player is signed in (and keeps them fresh). */
export function useLiveLoader(): void {
  const signedIn = useAccount((s) => s.snapshot.status === 'signedIn');
  const client = useAccount((s) => s.client);
  useEffect(() => {
    if (signedIn && client) void useLiveStore.getState().refresh();
  }, [signedIn, client]);
}

/** Whether a flag is on for this player: the service's word, or the app's default until it has one. */
export function useFlag(key: FlagKey): boolean {
  useLiveLoader();
  const flags = useLiveStore((s) => s.flags);
  const userId = useAccount((s) => s.snapshot.user?.id ?? null);
  return flagOn(flags, key, userId);
}

/** A value of the remote configuration, or `fallback` when there is none (or it is not what is expected). */
export function useConfig<T>(key: string, fallback: T, valid: (v: unknown) => v is T): T {
  useLiveLoader();
  const value = useLiveStore((s) => s.config[key]);
  return valid(value) ? value : fallback;
}

export type { LiveSnapshot };
